// src/pages/Production/ProfitabilityAnalysis.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    summaryCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
    },
    summaryCard: {
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    cardTitle: {
        fontSize: '14px',
        color: '#64748b',
        marginBottom: '8px',
        fontWeight: '500'
    },
    cardValue: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#0f172a'
    },
    cardSubtext: {
        fontSize: '12px',
        color: '#64748b',
        marginTop: '4px'
    },
    profitCard: {
        background: 'linear-gradient(135deg, #059669, #10b981)',
        color: 'white'
    },
    lossCard: {
        background: 'linear-gradient(135deg, #dc2626, #ef4444)',
        color: 'white'
    },
    searchContainer: {
        marginBottom: '24px',
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    searchInputContainer: {
        position: 'relative',
        maxWidth: '300px',
        flex: 1
    },
    searchIcon: {
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8'
    },
    searchInput: {
        padding: '12px 16px 12px 44px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        width: '100%',
        background: '#f8fafc',
        outline: 'none'
    },
    dateInput: {
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        background: '#f8fafc',
        outline: 'none'
    },
    tableContainer: {
        maxHeight: '60vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    trHoverActive: {
        background: '#f8fafc'
    },
    profitBadge: {
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600'
    },
    profitPositive: {
        background: '#dcfce7',
        color: '#166534'
    },
    profitNegative: {
        background: '#fef2f2',
        color: '#dc2626'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    progressBar: {
        height: '6px',
        background: '#e2e8f0',
        borderRadius: '3px',
        overflow: 'hidden',
        marginTop: '8px'
    },
    progressFill: {
        height: '100%',
        borderRadius: '3px',
        transition: 'width 0.3s ease'
    }
};

function ProfitabilityAnalysis() {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [rawMaterialPurchases, setRawMaterialPurchases] = useState([]);
    const [subcontractors, setSubcontractors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [hoveredRow, setHoveredRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Fetch all required data
    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Fetch products
        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch orders
        const unsubOrders = onSnapshot(
            query(collection(db, 'companies', companyId, 'orders'), orderBy('orderDate', 'desc')),
            snap => setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch recipes
        const unsubRecipes = onSnapshot(
            query(collection(db, 'companies', companyId, 'recipes')),
            snap => setRecipes(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch raw materials
        const unsubRawMaterials = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterials')),
            snap => setRawMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch raw material purchases
        const unsubPurchases = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterialPurchases')),
            snap => setRawMaterialPurchases(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch subcontractors
        const unsubSubcontractors = onSnapshot(
            query(collection(db, 'companies', companyId, 'suppliers')),
            snap => {
                setSubcontractors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }
        );

        return () => {
            unsubProducts();
            unsubOrders();
            unsubRecipes();
            unsubRawMaterials();
            unsubPurchases();
            unsubSubcontractors();
        };
    }, [userData]);

    // Helper function to get latest purchase price for a material
    const getMaterialPurchasePrice = (materialId, materialName) => {
        const purchasesWithMaterial = rawMaterialPurchases
            .filter(p => p.lineItems && p.lineItems.some(item =>
                item.productId === materialId || item.name === materialName || item.productName === materialName
            ))
            .sort((a, b) => {
                const dateA = a.purchaseDate?.toDate?.() || (a.purchaseDate?.seconds ? new Date(a.purchaseDate.seconds * 1000) : new Date(0));
                const dateB = b.purchaseDate?.toDate?.() || (b.purchaseDate?.seconds ? new Date(b.purchaseDate.seconds * 1000) : new Date(0));
                return dateB - dateA;
            });

        if (purchasesWithMaterial.length > 0) {
            const latestPurchase = purchasesWithMaterial[0];
            const lineItem = latestPurchase.lineItems.find(item =>
                item.productId === materialId || item.name === materialName || item.productName === materialName
            );
            if (lineItem && lineItem.price > 0) {
                return lineItem.price;
            }
        }
        return null;
    };

    // Filter orders by date
    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            if (!dateFrom && !dateTo) return true;

            const orderDate = order.orderDate?.toDate?.() ||
                (order.orderDate?.seconds ? new Date(order.orderDate.seconds * 1000) : null);

            if (!orderDate) return true;

            const from = dateFrom ? new Date(dateFrom) : null;
            const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;

            if (from && orderDate < from) return false;
            if (to && orderDate > to) return false;

            return true;
        });
    }, [orders, dateFrom, dateTo]);

    // Calculate profitability for each product
    const productsWithProfitability = useMemo(() => {
        // Combine all raw materials with purchase prices
        const allMaterials = [
            ...rawMaterials.map(rm => {
                const purchasePrice = getMaterialPurchasePrice(rm.id, rm.name);
                return {
                    id: rm.id,
                    name: rm.name,
                    unitPrice: purchasePrice || rm.unitPrice || 0,
                    unit: rm.unit || 'Adet'
                };
            }),
            ...products.filter(p => p.isRawMaterial).map(p => {
                const purchasePrice = getMaterialPurchasePrice(p.id, p.name);
                return {
                    id: p.id,
                    name: p.name,
                    unitPrice: purchasePrice || p.purchasePrice || p.salePrice || 0,
                    unit: p.unit || 'Adet'
                };
            })
        ];

        return products
            .filter(p => !p.isRawMaterial)
            .map(product => {
                // Calculate sales from orders
                let totalRevenue = 0;
                let totalQuantitySold = 0;
                let orderCount = 0;

                filteredOrders.forEach(order => {
                    if (order.lineItems) {
                        order.lineItems.forEach(item => {
                            if (item.productId === product.id || item.name === product.name) {
                                totalRevenue += item.total || (item.price * item.quantity) || 0;
                                totalQuantitySold += item.quantity || 0;
                                orderCount++;
                            }
                        });
                    }
                });

                // Calculate unit cost from recipe
                const recipe = recipes.find(r =>
                    r.product === product.name || r.productId === product.id
                );

                let rawMaterialCost = 0;
                let subcontractingCost = 0;
                let laborCost = 0;

                if (recipe && recipe.stages) {
                    recipe.stages.forEach(stage => {
                        // Raw material costs
                        if (stage.inputMaterials) {
                            stage.inputMaterials.forEach(material => {
                                const rawMat = allMaterials.find(rm =>
                                    rm.name === material.material || rm.id === material.materialId
                                );
                                if (rawMat) {
                                    rawMaterialCost += (rawMat.unitPrice || 0) * (material.quantity || 0);
                                }
                            });
                        }

                        // Subcontracting costs
                        if (stage.type === 'subcontracting' && stage.subcontractors) {
                            stage.subcontractors.forEach(sub => {
                                const supplier = subcontractors.find(s => s.id === sub.supplierId);
                                if (supplier && supplier.costTariff && supplier.costTariff[0]) {
                                    const tariff = supplier.costTariff[0];
                                    subcontractingCost += tariff.value || 0;
                                }
                                laborCost += sub.laborCost || 0;
                            });
                        }

                        // Internal labor costs
                        if (stage.type === 'internal' && stage.duration) {
                            laborCost += (stage.duration / 60) * 15; // 15 TRY/hour
                        }
                    });
                }

                const overheadCost = (rawMaterialCost + laborCost) * 0.10;
                const unitCost = rawMaterialCost + subcontractingCost + laborCost + overheadCost;
                const totalCost = unitCost * totalQuantitySold;
                const grossProfit = totalRevenue - totalCost;
                const profitMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;
                const averageSellingPrice = totalQuantitySold > 0 ? totalRevenue / totalQuantitySold : (product.salePrice || 0);

                return {
                    id: product.id,
                    name: product.name,
                    code: product.barcode || product.code || '',
                    totalRevenue: parseFloat(totalRevenue.toFixed(2)),
                    totalQuantitySold,
                    orderCount,
                    unitCost: parseFloat(unitCost.toFixed(2)),
                    totalCost: parseFloat(totalCost.toFixed(2)),
                    grossProfit: parseFloat(grossProfit.toFixed(2)),
                    profitMargin: parseFloat(profitMargin.toFixed(2)),
                    averageSellingPrice: parseFloat(averageSellingPrice.toFixed(2)),
                    hasRecipe: !!recipe,
                    costBreakdown: {
                        rawMaterials: parseFloat(rawMaterialCost.toFixed(2)),
                        subcontracting: parseFloat(subcontractingCost.toFixed(2)),
                        labor: parseFloat(laborCost.toFixed(2)),
                        overhead: parseFloat(overheadCost.toFixed(2))
                    }
                };
            });
    }, [products, filteredOrders, recipes, rawMaterials, rawMaterialPurchases, subcontractors]);

    // Calculate overall totals
    const totals = useMemo(() => {
        return productsWithProfitability.reduce((acc, product) => ({
            totalRevenue: acc.totalRevenue + product.totalRevenue,
            totalCost: acc.totalCost + product.totalCost,
            grossProfit: acc.grossProfit + product.grossProfit,
            totalQuantitySold: acc.totalQuantitySold + product.totalQuantitySold
        }), { totalRevenue: 0, totalCost: 0, grossProfit: 0, totalQuantitySold: 0 });
    }, [productsWithProfitability]);

    const overallProfitMargin = totals.totalRevenue > 0
        ? (totals.grossProfit / totals.totalRevenue) * 100
        : 0;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount);
    };

    const filteredProducts = productsWithProfitability.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.code && product.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Sort by profit margin (descending)
    const sortedProducts = [...filteredProducts].sort((a, b) => b.grossProfit - a.grossProfit);

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                        <p>Karlılık verileri hesaplanıyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Karlılık Analizi</h1>
                    <p style={{ color: '#64748b', marginTop: '4px' }}>
                        Ürün bazlı gelir, maliyet ve kâr analizi
                    </p>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={styles.summaryCards}>
                <div style={styles.summaryCard}>
                    <div style={styles.cardTitle}>📈 Toplam Gelir</div>
                    <div style={styles.cardValue}>{formatCurrency(totals.totalRevenue)}</div>
                    <div style={styles.cardSubtext}>{totals.totalQuantitySold} adet satış</div>
                </div>

                <div style={styles.summaryCard}>
                    <div style={styles.cardTitle}>📉 Toplam Maliyet</div>
                    <div style={styles.cardValue}>{formatCurrency(totals.totalCost)}</div>
                    <div style={styles.cardSubtext}>Hammadde + Fason + İşçilik + Genel</div>
                </div>

                <div style={{
                    ...styles.summaryCard,
                    ...(totals.grossProfit >= 0 ? styles.profitCard : styles.lossCard)
                }}>
                    <div style={{ ...styles.cardTitle, color: 'rgba(255,255,255,0.9)' }}>
                        {totals.grossProfit >= 0 ? '💰 Brüt Kâr' : '⚠️ Brüt Zarar'}
                    </div>
                    <div style={{ ...styles.cardValue, color: 'white' }}>
                        {formatCurrency(Math.abs(totals.grossProfit))}
                    </div>
                    <div style={{ ...styles.cardSubtext, color: 'rgba(255,255,255,0.8)' }}>
                        Gelir - Maliyet
                    </div>
                </div>

                <div style={styles.summaryCard}>
                    <div style={styles.cardTitle}>📊 Kâr Marjı</div>
                    <div style={{
                        ...styles.cardValue,
                        color: overallProfitMargin >= 0 ? '#059669' : '#dc2626'
                    }}>
                        %{overallProfitMargin.toFixed(1)}
                    </div>
                    <div style={styles.progressBar}>
                        <div style={{
                            ...styles.progressFill,
                            width: `${Math.min(Math.abs(overallProfitMargin), 100)}%`,
                            background: overallProfitMargin >= 0
                                ? 'linear-gradient(90deg, #059669, #10b981)'
                                : 'linear-gradient(90deg, #dc2626, #ef4444)'
                        }} />
                    </div>
                </div>
            </div>

            {/* Product List */}
            <div style={styles.box}>
                <div style={styles.searchContainer}>
                    <div style={styles.searchInputContainer}>
                        <span style={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            placeholder="Ürün adı veya kodu ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', color: '#64748b' }}>Tarih:</span>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            style={styles.dateInput}
                        />
                        <span style={{ color: '#64748b' }}>-</span>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            style={styles.dateInput}
                        />
                    </div>
                </div>

                {sortedProducts.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                        <h3 style={{ color: '#374151', marginBottom: '8px' }}>Veri Bulunamadı</h3>
                        <p>
                            {searchTerm
                                ? 'Arama kriterlerinize uygun ürün bulunamadı.'
                                : 'Henüz satış verisi bulunmuyor.'}
                        </p>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Ürün</th>
                                    <th style={styles.th}>Satış Adedi</th>
                                    <th style={styles.th}>Toplam Gelir</th>
                                    <th style={styles.th}>Birim Maliyet</th>
                                    <th style={styles.th}>Toplam Maliyet</th>
                                    <th style={styles.th}>Brüt Kâr</th>
                                    <th style={styles.th}>Kâr Marjı</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedProducts.map(product => (
                                    <tr
                                        key={product.id}
                                        style={{
                                            ...styles.trHover,
                                            ...(hoveredRow === product.id ? styles.trHoverActive : {})
                                        }}
                                        onMouseEnter={() => setHoveredRow(product.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        onClick={() => navigate(`/uretim/karlilik-analizi/${product.id}`)}
                                    >
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '600' }}>{product.name}</div>
                                            {product.code && (
                                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                    {product.code}
                                                </div>
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            {product.totalQuantitySold} adet
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                {product.orderCount} sipariş
                                            </div>
                                        </td>
                                        <td style={styles.td}>{formatCurrency(product.totalRevenue)}</td>
                                        <td style={styles.td}>
                                            {formatCurrency(product.unitCost)}
                                            {!product.hasRecipe && (
                                                <div style={{ fontSize: '10px', color: '#f59e0b' }}>
                                                    Reçete yok
                                                </div>
                                            )}
                                        </td>
                                        <td style={styles.td}>{formatCurrency(product.totalCost)}</td>
                                        <td style={{
                                            ...styles.td,
                                            fontWeight: '700',
                                            color: product.grossProfit >= 0 ? '#059669' : '#dc2626'
                                        }}>
                                            {formatCurrency(product.grossProfit)}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.profitBadge,
                                                ...(product.profitMargin >= 0 ? styles.profitPositive : styles.profitNegative)
                                            }}>
                                                %{product.profitMargin.toFixed(1)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProfitabilityAnalysis;
